import { TOOL_REQUIRED_CRITERIA } from './toolCriteria';
import { REGISTRY_SESSION_TOOL_IDS, REGISTRY_TOOL_SPECS_BY_ID } from '../generated/registryToolSpecs';
import { getOutputTypeForSessionTool } from './toolRegistry';
import { useAttributeGovernanceStore } from '../stores/prioritization/attributeGovernanceStore';
import type { PrioritizationSession } from '../types/prioritization/session';

export interface SessionToolDescriptor {
  id: string;
  aliases: string[];
  name: string;
  description: string;
  compatibilityId: string;
}

interface SessionToolBinding {
  uiId: string;
  aliases?: string[];
}

export const SESSION_TOOL_BINDINGS: Record<string, SessionToolBinding> = {
  pairwise_ranking: {
    uiId: 'pairwise',
    aliases: ['pairwise-comparison', 'pairwise-ranking'],
  },
  stack_ranking: {
    uiId: 'stack_ranking',
    aliases: ['stack-ranking', 'force-ranking', 'forced-ranking'],
  },
  dot_voting: {
    uiId: 'dot_voting',
    aliases: ['dot-voting'],
  },
  simple_weighted_scoring: {
    uiId: 'weighted_scoring',
    // 'simple-weighted-scoring' is the methodRegistry / shape-matrix toolId for this tool.
    aliases: ['weighted-scoring', 'simple-weighted-scoring'],
  },
  wsjf: {
    uiId: 'wsjf',
    aliases: ['weighted-shortest-job-first'],
  },
  cost_of_delay: {
    uiId: 'cost_of_delay',
    aliases: ['cost-of-delay'],
  },
  pareto_analysis: {
    uiId: 'pareto_analysis',
    aliases: ['pareto-analysis'],
  },
  buy_a_feature: {
    uiId: 'buy_a_feature',
    aliases: ['buy-a-feature'],
  },
  impact_effort_matrix: {
    uiId: 'impact_effort',
    // 'impact-effort-matrix' is the methodRegistry / shape-matrix toolId for this tool.
    aliases: ['impact-effort', 'impact-effort-matrix'],
  },
  rice: {
    uiId: 'rice',
    aliases: ['rice-scoring'],
  },
  moscow: {
    uiId: 'moscow',
    aliases: ['moscow-method'],
  },
  max_priorities_pyramid: {
    uiId: 'max_priorities_pyramid',
    aliases: ['max-priorities-pyramid'],
  },
  magic_prioritization: {
    uiId: 'magic_prioritization',
    aliases: ['magic-prioritization'],
  },
  ahp: {
    uiId: 'ahp',
    aliases: ['analytic-hierarchy-process'],
  },
  fast_vs_right: {
    uiId: 'fast_vs_right',
    aliases: ['fast-vs-right'],
  },
  eisenhower_matrix: {
    uiId: 'eisenhower_matrix',
    aliases: ['eisenhower-matrix'],
  },
  speed_boat: {
    uiId: 'speed_boat',
    aliases: ['speed-boat'],
  },
  prune_product_tree: {
    uiId: 'prune_product_tree',
    aliases: ['prune-product-tree', 'prune-the-tree'],
  },
};

// TOOL_REQUIRED_CRITERIA and toolAsksForCriteria live in ./toolCriteria (no
// store imports, so the pure Event Designer logic can use them); re-exported
// here for existing callers.
export { TOOL_REQUIRED_CRITERIA, toolAsksForCriteria } from './toolCriteria';

function unique(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean)));
}

export const SESSION_TOOL_DESCRIPTORS: SessionToolDescriptor[] = REGISTRY_SESSION_TOOL_IDS.map((toolId) => {
  const spec = REGISTRY_TOOL_SPECS_BY_ID[toolId];
  const binding = SESSION_TOOL_BINDINGS[toolId];

  if (!spec) {
    throw new Error(`Missing registry spec for session tool: ${toolId}`);
  }

  if (!binding) {
    throw new Error(`Missing session UI binding for registry tool: ${toolId}`);
  }

  return {
    id: binding.uiId,
    aliases: unique([...(binding.aliases ?? []), toolId]),
    name: spec.displayName,
    description: spec.description,
    compatibilityId: spec.toolId,
  };
});

export const SESSION_TOOL_DESCRIPTOR_BY_COMPATIBILITY_ID: Record<string, SessionToolDescriptor> = Object.fromEntries(
  SESSION_TOOL_DESCRIPTORS.map((descriptor) => [descriptor.compatibilityId, descriptor])
);

export const SESSION_TOOL_DESCRIPTOR_BY_ANY_ID: Record<string, SessionToolDescriptor> = Object.fromEntries(
  SESSION_TOOL_DESCRIPTORS.flatMap((descriptor) =>
    unique([descriptor.id, descriptor.compatibilityId, ...descriptor.aliases]).map((toolId) => [toolId, descriptor])
  )
);

export function resolveSessionToolDescriptor(toolId?: string | null): SessionToolDescriptor | undefined {
  if (!toolId) {
    return undefined;
  }

  return SESSION_TOOL_DESCRIPTOR_BY_ANY_ID[toolId];
}

/**
 * Normalize any session tool identifier to the canonical registry id when available.
 * Unknown ids are preserved so older or external callers fail soft.
 */
export function normalizeSessionToolId(toolId?: string | null): string | undefined {
  if (!toolId) {
    return undefined;
  }

  return resolveSessionToolDescriptor(toolId)?.compatibilityId ?? toolId;
}

/**
 * True when a session may run or close without an explicit "finalize criteria" step.
 * Covers: criteria on the session, track-locked criteria, tools with built-in dimensions
 * (Impact/Effort, RICE, etc.), or tools that do not require workspace criteria.
 */
export function sessionHasObviousOrLockedCriteria(
  session: Pick<PrioritizationSession, 'selectedToolId' | 'criteriaConfig' | 'trackId' | 'state'>
): boolean {
  if (session.state !== 'DRAFT') return true;
  if (session.criteriaConfig.criteriaIds.length > 0) return true;

  const canonical = normalizeSessionToolId(session.selectedToolId);
  if (canonical) {
    const spec = REGISTRY_TOOL_SPECS_BY_ID[canonical];
    if (spec?.compatibility.toolDefinesCriteria) return true;
    if ((TOOL_REQUIRED_CRITERIA[canonical] ?? []).length > 0) return true;
    if (spec && !spec.compatibility.requiresCriteria) return true;
    // MoSCoW, Eisenhower, etc. — categories are assigned inside the tool, not via workspace criteria rows.
    if (getOutputTypeForSessionTool(canonical) === 'classification') return true;
  }

  if (session.trackId) {
    const trackConfigs = useAttributeGovernanceStore.getState().getTrackConfigs(session.trackId);
    if (trackConfigs.some(c => c.rangeStatus === 'accepted')) return true;
  }

  return false;
}
